import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Pagination, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

// Blocklist (backend/lib/danh_sach_chan.js): he A refuses register, login, deposit order, withdrawal,
// bank link and giftcode for a matching bank account, Telegram account, device or IP.
type Row = {
  id: number; loai: Loai; gia_tri: string; ly_do: string | null; nguoi: string | null; tao_luc: string | null;
  go_luc: string | null; go_boi: string | null; go_ly_do: string | null;
};
type Loai = 'ngan_hang' | 'telegram' | 'thiet_bi' | 'ip';
type Page = { items: Row[]; total: number };

const LOAI: Record<Loai, { label: string; help: string; placeholder: string }> = {
  ngan_hang: { label: 'Tài khoản ngân hàng', help: 'Số tài khoản (chỉ chữ số). Chặn mọi nick từng liên kết số này và lệnh rút về số này.', placeholder: '1002003004' },
  telegram: { label: 'Telegram (thay số điện thoại)', help: 'Chat id Telegram người chơi đã liên kết (trang người chơi, tab Bảo mật). Game không thu số điện thoại.', placeholder: '123456789' },
  thiet_bi: { label: 'Thiết bị', help: 'Mã thiết bị client gửi lúc đăng nhập (trang người chơi, lịch sử đăng nhập).', placeholder: 'fg...' },
  ip: { label: 'IP hoặc dải IP', help: 'Một địa chỉ hoặc một dải CIDR, từ /8 (IPv4) hay /16 (IPv6) trở xuống.', placeholder: '203.0.113.0/24' },
};

const FIELDS: FilterField[] = [
  { key: 'active', label: 'Trạng thái', kind: 'select', options: [['1', 'Đang chặn'], ['0', 'Đã gỡ'], ['', 'Tất cả']] },
  { key: 'loai', label: 'Loại', kind: 'select', options: [['', 'Tất cả'], ...Object.entries(LOAI).map(([k, v]) => [k, v.label] as [string, string])] },
  { key: 'q', label: 'Giá trị hoặc lý do', kind: 'text' },
];
const KEYS = FIELDS.map((x) => x.key);

const columns: ColumnDef<Row, any>[] = [
  { header: 'Loại', accessorKey: 'loai', cell: (c) => LOAI[c.getValue() as Loai]?.label ?? c.getValue() },
  { header: 'Giá trị', accessorKey: 'gia_tri', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Lý do', accessorKey: 'ly_do', cell: (c) => c.getValue() ?? '—' },
  { header: 'Lúc', accessorKey: 'tao_luc', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Trạng thái', id: 'st', cell: (c) => c.row.original.go_luc
    ? <span title={c.row.original.go_ly_do ?? undefined}><Tag size="sm" type="gray">Đã gỡ</Tag> <span className="muted">{c.row.original.go_boi ?? ''}</span></span>
    : <Tag size="sm" type="red">Đang chặn</Tag> },
];

export function BlocklistPage() {
  const qc = useQueryClient();
  const f = useUrlFilters(KEYS, { defaults: { active: '1' } });
  const { page, pageSize } = f;
  const q = useQuery({
    queryKey: ['blocklist', f.values, page, pageSize],
    queryFn: () => api.get<Page>('/api/admin/risk/blocklist', { ...f.query, page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const [adding, setAdding] = useState(false);
  const [loai, setLoai] = useState<Loai>('ngan_hang');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [pick, setPick] = useState<Row | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const done = (msg: string) => { setNotice(msg); qc.invalidateQueries({ queryKey: ['blocklist'] }); };
  const add = useMutation({
    mutationFn: () => api.post<{ id: number; existed: boolean; value: string }>('/api/admin/risk/blocklist', { loai, value: value.trim(), reason: reason.trim() }),
    onSuccess: (r) => { setAdding(false); setValue(''); setReason(''); done(r.existed ? `${r.value} đã có trong danh sách chặn.` : `Đã chặn ${r.value}. Hệ A từ chối ngay từ thao tác tiếp theo.`); },
  });
  const remove = useMutation({
    mutationFn: (r: Row) => api.post(`/api/admin/risk/blocklist/${r.id}/remove`, { reason: removeReason.trim() }),
    onSuccess: () => { setPick(null); setRemoveReason(''); done('Đã gỡ chặn.'); },
  });
  return (
    <div className="page">
      <PageHeader title="Danh sách chặn"
        lead="Tài khoản ngân hàng, Telegram, thiết bị hoặc IP bị chặn không đăng ký, đăng nhập, tạo lệnh nạp, rút, liên kết ngân hàng hay nhận giftcode được. Người chơi chỉ thấy câu “tài khoản hoặc thiết bị đang bị hạn chế”, không biết danh sách nào khớp. Gỡ chặn giữ lại lịch sử." />
      {notice ? <InlineNotification kind="success" lowContrast title={notice} onClose={() => setNotice(null)} /> : null}
      <FilterBar id="bl" fields={FIELDS} filters={f}>
        <Button size="md" onClick={() => { add.reset(); setAdding(true); }}>Thêm chặn</Button>
      </FilterBar>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} onRowClick={(r) => { if (!r.go_luc) { remove.reset(); setRemoveReason(''); setPick(r); } }} rowKey={(r) => String(r.id)}
        empty={{ title: 'Chưa chặn gì', body: 'Dòng chặn mới hiện ở đây.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={adding} modalHeading="Thêm chặn" primaryButtonText="Chặn" secondaryButtonText="Hủy" danger
        primaryButtonDisabled={add.isPending || !value.trim() || reason.trim().length < 3}
        onRequestClose={() => setAdding(false)} onRequestSubmit={() => add.mutate()}>
        <div className="stack">
          <Select id="bl-loai" labelText="Loại" value={loai} onChange={(e) => setLoai(e.target.value as Loai)}>
            {Object.entries(LOAI).map(([k, v]) => <SelectItem key={k} value={k} text={v.label} />)}
          </Select>
          <TextInput id="bl-value" labelText="Giá trị" helperText={LOAI[loai].help} placeholder={LOAI[loai].placeholder} value={value} maxLength={100}
            onChange={(e) => setValue(e.target.value)} />
          <TextInput id="bl-reason" labelText="Lý do (bắt buộc)" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
          <InlineError error={add.error} />
        </div>
      </Modal>
      <Modal open={!!pick} modalHeading="Gỡ chặn?" primaryButtonText="Gỡ chặn" secondaryButtonText="Hủy"
        primaryButtonDisabled={remove.isPending || removeReason.trim().length < 3}
        onRequestClose={() => setPick(null)} onRequestSubmit={() => pick && remove.mutate(pick)}>
        <div className="stack">
          {pick ? <p>{LOAI[pick.loai]?.label ?? pick.loai}: <strong className="num">{pick.gia_tri}</strong>{pick.ly_do ? <span className="muted"> · {pick.ly_do}</span> : null}</p> : null}
          <TextInput id="bl-remove-reason" labelText="Lý do gỡ (bắt buộc)" value={removeReason} maxLength={200} onChange={(e) => setRemoveReason(e.target.value)} />
          <InlineError error={remove.error} />
        </div>
      </Modal>
    </div>
  );
}
